import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Clock, CreditCard, ArrowLeft, Check, Copy, Zap, MessageSquare, Users, Bot, Instagram } from "lucide-react";
import { walletClienteAPI } from "../services/api";
import { planosAPI } from "../services/planosService";
import QRCode from "react-qr-code";
import "../global.css";

const ContaSuspensa = () => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const clienteId = location.state?.cliente_id;
  const motivo = location.state?.motivo || 'trial_expirado';
  const isInadimplente = motivo === 'inadimplencia';
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cobranca, setCobranca] = useState(null);
  const [pago, setPago] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!clienteId) { navigate("/login"); return; }
    planosAPI.list().then(r => setPlanos((r.data || []).filter(p => parseFloat(p.preco) > 0))).catch(() => {});
  }, [clienteId, navigate]);

  useEffect(() => {
    if (!cobranca?.codigoSolicitacao || pago) return;
    const interval = setInterval(async () => {
      try {
        const res = await walletClienteAPI.consultarCobranca(cobranca.codigoSolicitacao);
        if (res.data?.status === "PAGA") { setPago(true); clearInterval(interval); }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [cobranca, pago]);

  const assinar = async (planoId) => {
    setLoading(planoId);
    try {
      const res = await walletClienteAPI.upgrade(planoId);
      setCobranca(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || "Erro ao gerar cobrança");
    } finally { setLoading(false); }
  };

  const copyPix = () => {
    navigator.clipboard.writeText(cobranca.pixCopiaECola);
    setCopied(true);
    toast.success("Código Pix copiado!");
    setTimeout(() => setCopied(false), 3000);
  };

  const bg = darkMode ? "content-bg-dark" : "bg-gradient-to-br from-gray-50 to-gray-100";
  const card = darkMode ? "bg-zinc-800/90 border border-zinc-700/50 backdrop-blur" : "bg-white border border-gray-200 shadow-lg";
  const t1 = darkMode ? "text-white" : "text-gray-900";
  const t2 = darkMode ? "text-zinc-400" : "text-gray-500";

  // Tela de pagamento confirmado
  if (pago) return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bg}`}>
      <div className={`max-w-md w-full rounded-3xl p-10 text-center ${card}`}>
        <div className="relative mx-auto w-20 h-20 mb-6">
          <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
          <div className="relative w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Check className="h-10 w-10 text-emerald-500" />
          </div>
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${t1}`}>Pagamento confirmado! 🎉</h2>
        <p className={`mb-8 ${t2}`}>Seu plano foi ativado com sucesso.</p>
        <button onClick={() => navigate("/login")}
          className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-nomoz-orange to-orange-600 hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/25">
          Acessar plataforma →
        </button>
      </div>
    </div>
  );

  // Tela de pagamento Pix
  if (cobranca) return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bg}`}>
      <div className={`max-w-md w-full rounded-3xl overflow-hidden ${card}`}>
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-5 text-white">
          <p className="text-sm opacity-90">Plano {cobranca.plano?.nome}</p>
          <p className="text-3xl font-bold mt-1">R$ {parseFloat(cobranca.valor).toFixed(2)}</p>
          <p className="text-xs opacity-75 mt-1">Vence em {cobranca.dataVencimento}</p>
        </div>
        <div className="p-6 space-y-5">
          {cobranca.pixCopiaECola && (
            <>
              <div className="flex justify-center p-4 bg-white rounded-2xl">
                <QRCode value={cobranca.pixCopiaECola} size={180} />
              </div>
              <button onClick={copyPix}
                className={`w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                  copied ? 'bg-emerald-500 text-white' : 'border-2 border-emerald-500 text-emerald-500 hover:bg-emerald-500/10'
                }`}>
                {copied ? <><Check className="h-4 w-4" /> Copiado!</> : <><Copy className="h-4 w-4" /> Copiar código Pix</>}
              </button>
            </>
          )}
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-500 border-t-transparent" />
            <p className={`text-sm ${t2}`}>Aguardando pagamento...</p>
          </div>
          <button onClick={() => { setCobranca(null); setCopied(false); }}
            className={`w-full text-sm ${t2} hover:underline`}>← Escolher outro plano</button>
        </div>
      </div>
    </div>
  );

  // Tela principal - escolha de plano
  const popular = planos.length >= 2 ? planos[1] : null; // segundo plano = recomendado

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${bg}`}>
      <div className="max-w-4xl w-full space-y-8">

        {/* Header */}
        <div className="text-center">
          <img src="/assets/logo_nomoz.png" width={140} height={50} alt="Nomoz" className="mx-auto mb-6" />
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 ${darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">{isInadimplente ? 'Conta suspensa por inadimplência' : 'Seu período de teste expirou'}</span>
          </div>
          <h1 className={`text-3xl font-bold ${t1}`}>{isInadimplente ? 'Regularize seu pagamento' : 'Escolha o plano ideal para você'}</h1>
          <p className={`mt-2 ${t2}`}>{isInadimplente ? 'Pague a fatura pendente para reativar sua conta' : 'Continue usando a Nomoz sem interrupção'}</p>
        </div>

        {/* Planos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {planos.map(plano => {
            const isPopular = popular?.id === plano.id;
            return (
              <div key={plano.id} className={`rounded-3xl p-6 flex flex-col relative transition-all hover:scale-[1.02] ${
                isPopular
                  ? 'bg-gradient-to-b from-orange-500 to-orange-600 text-white shadow-xl shadow-orange-500/25 ring-2 ring-orange-400'
                  : card
              }`}>
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold bg-white text-orange-600 shadow-md">
                    ⭐ MAIS POPULAR
                  </span>
                )}
                <h3 className={`text-lg font-bold ${isPopular ? 'text-white' : t1}`}>{plano.nome}</h3>
                <div className="mt-2 mb-5">
                  <span className={`text-3xl font-bold ${isPopular ? 'text-white' : t1}`}>R$ {parseFloat(plano.preco).toFixed(0)}</span>
                  <span className={`text-sm ${isPopular ? 'text-white/70' : t2}`}>/mês</span>
                </div>
                <div className={`space-y-2.5 text-sm flex-1 ${isPopular ? 'text-white/90' : t2}`}>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 shrink-0" />
                    {plano.limite_mensagens_mes === -1 ? "Mensagens ilimitadas" : `${plano.limite_mensagens_mes?.toLocaleString()} msgs/mês`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 shrink-0" />
                    {plano.limite_canais_whatsapp === -1 ? "WhatsApp ilimitado" : `${plano.limite_canais_whatsapp} canal WhatsApp`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 shrink-0" />
                    {plano.limite_usuarios === -1 ? "Usuários ilimitados" : `${plano.limite_usuarios} usuário${plano.limite_usuarios > 1 ? 's' : ''}`}
                  </div>
                  {plano.permite_ia && <div className="flex items-center gap-2"><Bot className="h-4 w-4 shrink-0" /> IA integrada</div>}
                  {plano.permite_instagram && <div className="flex items-center gap-2"><Instagram className="h-4 w-4 shrink-0" /> Instagram</div>}
                  {plano.permite_fluxos && <div className="flex items-center gap-2"><Zap className="h-4 w-4 shrink-0" /> Automação</div>}
                </div>
                <div className="mt-6">
                  <button onClick={() => assinar(plano.id)} disabled={loading === plano.id}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 ${
                      isPopular
                        ? 'bg-white text-orange-600 hover:bg-orange-50 shadow-md'
                        : 'text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                    }`}>
                    {loading === plano.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mx-auto" /> : "Assinar agora"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link to="/login" className={`text-sm ${t2} hover:underline inline-flex items-center gap-1`}>
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContaSuspensa;
